import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import Loading from "components/Loading";
import UserContext from "./UserContext";
import API from "utils/api";
import "./style.scss";

const ACCESS_TOKEN_REFRESH_TIME = 1000 * 60 * 10; // Refresh timer for access tokens
const UNREAD_COUNTS_REFRESH_TIME = 1000 * 60 * 5; // Refresh timer for unread counts

function UserProvider({ children }) {
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem("token") || null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [tagBlacklist, setTagBlacklist] = useState(null);
  const [balance, setBalance] = useState(0);
  const [notifications, setNotifications] = useState(null);
  const [counts, setCounts] = useState({ messages: 0, notifications: 0 });

  // Store a refresh token in localStorage before
  // setting it in the local state.
  function storeRefreshToken(newRefreshToken) {
    if (newRefreshToken === null) {
      localStorage.removeItem("token");
      setRefreshToken(null);
      setAccessToken(null);
    } else {
      localStorage.setItem("token", newRefreshToken);
      setAccessToken(null);
      setRefreshToken(newRefreshToken);
    }
  }

  // Fetch a new accessToken using the refreshToken
  async function fetchAccessToken() {
    if (refreshToken === null) {
      fetchCurrentUser();
      return;
    }

    const { response, data } = await API.fetchAccessToken(refreshToken);

    if (response.ok) {
      setAccessToken(data.accessToken);
    } else {
      setRefreshToken(null);
      setAccessToken(null);
    }
  }

  async function fetchCurrentUser() {
    // Save fetching user if anonymous
    if (!accessToken && !refreshToken) {
      setUser({ id: null, username: null, role: "anonymous" });
      setProfile(null);
      setTagBlacklist([]);
      setNotifications({});
      setCounts({});
      return;
    } else if (!accessToken) {
      return;
    }

    const { data, response } = await API.fetchCurrentUser(accessToken);

    // Anonymous users get an Unauthorized response with basic data
    if (response.ok || response.status === 401) {
      setUser(data.user);
      setProfile(data.profile);
      setBalance(data.balance);
      setTagBlacklist(data.tagBlacklist);
      setNotifications(data.notifications);
      setCounts(data.counts);
    }
  }

  async function fetchCounts() {
    const { data, response } = await API.fetchCounts(accessToken);

    if (response.ok) {
      setCounts(data);
    }
  }

  useEffect(() => {
    fetchAccessToken();

    const interval = setInterval(() => {
      fetchAccessToken();
    }, ACCESS_TOKEN_REFRESH_TIME);

    return () => clearInterval(interval);
  }, [refreshToken]);

  useEffect(() => {
    fetchCurrentUser();
  }, [accessToken]);

  useEffect(() => {
    let interval = null;

    if (user?.id) {
      fetchCounts();

      interval = setInterval(() => {
        fetchCounts();
      }, UNREAD_COUNTS_REFRESH_TIME);
    }

    return () => clearInterval(interval);
  }, [user?.id]);


  // If no user has been fetched show the loading screen.
  if (user === null) {
    return (
      <div className="userProvider userProvider--loading">
        <Loading />
      </div>
    );
  }

  return (
    <UserContext.Provider value={{
      token: accessToken,
      setToken: storeRefreshToken,
      user,
      balance,
      profile,
      notifications,
      tagBlacklist: tagBlacklist ?? [],
      counts,
      reload: fetchCurrentUser,
      reloadCounts: fetchCounts,
    }}>
      {children}
    </UserContext.Provider>
  );
}

UserProvider.propTypes = {
  children: PropTypes.any,
};

export default UserProvider;
